import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme, getRtlTheme } from '../themes/theme';
import { useTranslation } from 'react-i18next';

// Create theme context
export const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
});

// Custom hook to use the theme context
export const useThemeContext = () => useContext(ThemeContext);

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Get stored theme preference or default to light
  const storedTheme = localStorage.getItem('theme') || 'light';
  const [mode, setMode] = useState(storedTheme);
  
  // Get current language for RTL support
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  
  // Force re-render when language changes
  const [themeKey, setThemeKey] = useState(0);
  
  // Get the appropriate theme based on mode and RTL
  const theme = React.useMemo(() => {
    const baseTheme = mode === 'dark' ? darkTheme : lightTheme;
    return isRtl ? getRtlTheme(baseTheme) : baseTheme;
  }, [mode, isRtl, themeKey]);

  // Toggle between light and dark mode
  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('theme', newMode);
  };

  // Listen for language changes to update RTL direction
  useEffect(() => {
    // Force theme update when language changes
    setThemeKey(prev => prev + 1);
  }, [i18n.language]);

  // Context value
  const contextValue = {
    mode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;